import { apiRequest } from "./backendApi";

export type MonitoredPair = {
  id: string;
  label: string;
  datasetName?: string | null;
};

export type LiveMetricRow = {
  id: string;
  metricName: string;
  currentValue: number;
  window: string;
  referenceValue?: number;
  referenceEnabled?: boolean;
};

export type MetricReferenceThreshold = {
  metric: string;
  window: string;
  value: number;
  enabled: boolean;
};

export type MetricReferencePayload = {
  agentId: string;
  datasetId: string;
  thresholds: MetricReferenceThreshold[];
  updatedAt: string;
  updatedBy: string;
};

export type PairMetricsPayload = {
  agentId?: string;
  datasetId?: string;
  datasetName?: string | null;
  nightly: boolean;
  stale?: boolean;
  lastRun?: string | null;
  refreshMessage?: string;
  refreshOutcome?: string;
  liveMetrics: LiveMetricRow[];
};

export type ItemEvaluation = {
  scoreName: string;
  score: number;
  passed: boolean;
  reason: string;
  basis: "verified_reference" | "judge_only";
  referenceSource: string | null;
  referenceConfidence: number | null;
  judgeModel: string;
  promptVersion: string;
  rubric: string;
  evaluatedAt: string;
};

type ItemEvaluationResponse = {
  score_name: string;
  score: number;
  passed: boolean;
  reason: string;
  basis: "verified_reference" | "judge_only";
  reference_source?: string | null;
  reference_confidence?: number | null;
  judge_model: string;
  prompt_version: string;
  rubric: string;
  evaluated_at: string;
};

function mapItemEvaluation(value?: ItemEvaluationResponse | null): ItemEvaluation | null {
  if (!value) return null;
  return {
    scoreName: value.score_name,
    score: value.score,
    passed: value.passed,
    reason: value.reason,
    basis: value.basis,
    referenceSource: value.reference_source ?? null,
    referenceConfidence: value.reference_confidence ?? null,
    judgeModel: value.judge_model,
    promptVersion: value.prompt_version,
    rubric: value.rubric,
    evaluatedAt: value.evaluated_at,
  };
}

export type PairDashboardItem = {
  itemIndex: number;
  traceId: string | null;
  status: string;
  scores: Record<string, number>;
  scoreReasons?: Record<string, string>;
  inputPreview: string | null;
  outputPreview: string | null;
  input?: unknown;
  output?: string | null;
  expectedOutput?: unknown;
  evaluation?: ItemEvaluation | null;
  error: string | null;
  demoCase: string | null;
};

export type PairDashboardRunPoint = {
  runId: string;
  label: string;
  completedAt: string | null;
  successRate: number;
  correctness: number | null;
  metricName: string | null;
  scores: Record<string, number>;
  traceCount: number;
};

export type PairDashboardExperimentPoint = {
  agentId: string;
  datasetId: string;
  runId: string | null;
  label: string;
  correctness: number | null;
  availability: number | null;
  metricName: string | null;
  selected: boolean;
};

export type ModelTrendPoint = {
  runId: string | null;
  comparisonId: string | null;
  label: string;
  createdAt: string | null;
  correctness: number | null;
  availability: number | null;
  selected: boolean;
};

export type ModelTrendSeries = {
  agentId: string;
  agentName: string | null;
  model: string | null;
  points: ModelTrendPoint[];
};

export type PairDashboardPayload = {
  agentId: string;
  datasetId: string;
  datasetName: string | null;
  agentName: string | null;
  model: string | null;
  connectorType: string | null;
  mode: string | null;
  runId: string | null;
  runCompletedAt: string | null;
  traceCount: number;
  itemTotal: number;
  costUsd: number | null;
  correctnessMetric: string | null;
  aggregates: Record<string, number>;
  items: PairDashboardItem[];
  runHistory: PairDashboardRunPoint[];
  experimentScores: PairDashboardExperimentPoint[];
  modelTrends: ModelTrendSeries[];
};

export type ModelComparisonEntry = {
  agentId: string;
  agentName: string | null;
  model: string | null;
  runId: string;
  status: string;
  correctness: number | null;
  availability: number | null;
  itemCount: number;
  isWinner: boolean;
};

export type ModelComparisonGroup = {
  comparisonId: string;
  datasetId: string;
  createdAt: string;
  models: ModelComparisonEntry[];
};

type PairRow = {
  agent_id: string;
  dataset_id: string;
  dataset_name: string | null;
  nightly: boolean;
  created_at: string;
  updated_at: string;
};

type PairsResponse = {
  data: PairRow[];
  count: number;
};

type PairMetricsResponse = {
  agent_id: string;
  dataset_id: string;
  dataset_name: string | null;
  nightly: boolean;
  stale: boolean;
  last_run: string | null;
  metrics: Array<{
    metric: string;
    window: "latest";
    current_value: number;
    reference_value?: number;
    reference_enabled?: boolean;
  }>;
};

type PairDashboardResponse = {
  agent_id: string;
  dataset_id: string;
  dataset_name: string | null;
  agent_name: string | null;
  model: string | null;
  connector_type: string | null;
  mode: string | null;
  run_id: string | null;
  run_completed_at: string | null;
  trace_count: number;
  item_total: number;
  cost_usd: number | null;
  correctness_metric: string | null;
  aggregates: Record<string, number>;
  items: Array<{
    item_index: number;
    trace_id: string | null;
    status: string;
    scores: Record<string, number>;
    score_reasons?: Record<string, string>;
    input_preview: string | null;
    output_preview: string | null;
    input?: unknown;
    output?: string | null;
    expected_output?: unknown;
    evaluation?: ItemEvaluationResponse | null;
    error: string | null;
    demo_case: string | null;
  }>;
  run_history: Array<{
    run_id: string;
    label: string;
    completed_at: string | null;
    success_rate: number;
    correctness: number | null;
    metric_name: string | null;
    scores: Record<string, number>;
    trace_count: number;
  }>;
  experiment_scores: Array<{
    agent_id: string;
    dataset_id: string;
    run_id: string | null;
    label: string;
    correctness: number | null;
    availability: number | null;
    metric_name: string | null;
    selected: boolean;
  }>;
  model_trends: Array<{
    agent_id: string;
    agent_name: string | null;
    model: string | null;
    points: Array<{
      run_id: string | null;
      comparison_id: string | null;
      label: string;
      created_at: string | null;
      correctness: number | null;
      availability: number | null;
      selected: boolean;
    }>;
  }>;
};

type MetricsAggregationReport = {
  entries: Array<{
    agent_id: string;
    dataset_id: string;
    outcome: string;
    reason?: string;
    metric_count?: number;
  }>;
};

type MetricReferenceResponse = {
  agent_id: string;
  dataset_id: string;
  thresholds: Array<{
    metric: string;
    window: string;
    value: number;
    enabled: boolean;
  }>;
  updated_at: string;
  updated_by: string;
};

export const DEMO_DEFAULT_AGENT_ID = "banking-finance-qa-gpt4o-mini";
export const DEMO_DEFAULT_DATASET_MATCH = "demo-cases";

export function resolveDefaultDatasetId(
  datasets: Array<{ id: string; label: string }>,
): string {
  const demo = datasets.find(
    (dataset) =>
      dataset.label.toLowerCase().includes(DEMO_DEFAULT_DATASET_MATCH) ||
      dataset.id.toLowerCase().includes(DEMO_DEFAULT_DATASET_MATCH),
  );
  return demo?.id ?? datasets[0]?.id ?? "";
}

export const pairKey = (agentId: string, datasetId: string) => `${agentId}::${datasetId}`;

export const parsePairId = (id: string) => {
  const sep = id.indexOf("::");
  if (sep < 0) return { agentId: id, datasetId: "" };
  return { agentId: id.slice(0, sep), datasetId: id.slice(sep + 2) };
};

export type DatasetOption = {
  id: string;
  label: string;
  pairs: MonitoredPair[];
};

export function resolveDatasetLabel(pair: MonitoredPair, datasetId: string): string {
  if (pair.datasetName?.trim()) return pair.datasetName.trim();
  if (pair.label.includes(" · ")) {
    const fromLabel = pair.label.split(" · ").slice(1).join(" · ").trim();
    if (fromLabel) return fromLabel;
  }
  if (pair.label.includes(" + ")) {
    const fromLabel = pair.label.split(" + ").slice(1).join(" + ").trim();
    if (fromLabel) return fromLabel;
  }
  return datasetId;
}

export function groupPairsByDataset(pairs: MonitoredPair[]): DatasetOption[] {
  // Group by logical dataset label so sibling Langfuse UUIDs with the same name
  // (Evaluate vs Dashboard) appear as one dropdown entry with all agents.
  const byLabel = new Map<string, DatasetOption>();
  for (const pair of pairs) {
    const { datasetId, agentId } = parsePairId(pair.id);
    if (!datasetId) continue;
    const label = resolveDatasetLabel(pair, datasetId);
    const key = label.trim().toLowerCase() || datasetId;
    const existing = byLabel.get(key);
    if (existing) {
      existing.pairs.push(pair);
      // Prefer the demo default agent's dataset UUID as the canonical id.
      if (agentId === DEMO_DEFAULT_AGENT_ID) {
        existing.id = datasetId;
      }
      continue;
    }
    byLabel.set(key, {
      id: datasetId,
      label,
      pairs: [pair],
    });
  }
  return Array.from(byLabel.values()).sort((a, b) => a.label.localeCompare(b.label));
}

export function findPairForAgent(pairs: MonitoredPair[], agentId: string): MonitoredPair | null {
  return pairs.find((pair) => parsePairId(pair.id).agentId === agentId) ?? null;
}

export function resolveDefaultAgentRunId(
  dashboard: PairDashboardPayload,
  agentId: string = DEMO_DEFAULT_AGENT_ID,
): string | null {
  const series = dashboard.modelTrends.find((entry) => entry.agentId === agentId);
  if (series && series.points.length > 0) {
    const inWindow = series.points.filter((point) => isWithinDashboardWindow(point.createdAt));
    const latest = inWindow[inWindow.length - 1] ?? series.points[series.points.length - 1];
    return latest?.runId ?? null;
  }
  if (dashboard.agentId === agentId) {
    return dashboard.runId;
  }
  return null;
}

const DASHBOARD_WINDOW_DAYS = 7;

function isWithinDashboardWindow(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return false;
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - DASHBOARD_WINDOW_DAYS);
  start.setUTCHours(0, 0, 0, 0);
  return ts >= start.getTime();
}

const listPairsRaw = async (): Promise<PairRow[]> => {
  const res = await apiRequest<PairsResponse>("GET", "/api/pairs");
  return res.data;
};

const pairMetricsPath = (agentId: string, datasetId: string) =>
  `/api/metrics/pairs/${encodeURIComponent(agentId)}/${encodeURIComponent(datasetId)}`;

const toPayload = (row: PairMetricsResponse): PairMetricsPayload => ({
  agentId: row.agent_id,
  datasetId: row.dataset_id,
  datasetName: row.dataset_name,
  nightly: row.nightly,
  stale: row.stale,
  lastRun: row.last_run,
  liveMetrics: row.metrics.map((metric) => ({
    id: `${metric.metric}:${metric.window}`,
    metricName: metric.metric,
    currentValue: metric.current_value,
    window: metric.window,
    referenceValue: metric.reference_value,
    referenceEnabled: metric.reference_enabled,
  })),
});

const toDashboardPayload = (row: PairDashboardResponse): PairDashboardPayload => ({
  agentId: row.agent_id,
  datasetId: row.dataset_id,
  datasetName: row.dataset_name,
  agentName: row.agent_name,
  model: row.model,
  connectorType: row.connector_type,
  mode: row.mode,
  runId: row.run_id,
  runCompletedAt: row.run_completed_at,
  traceCount: row.trace_count,
  itemTotal: row.item_total,
  costUsd: row.cost_usd,
  correctnessMetric: row.correctness_metric,
  aggregates: row.aggregates,
  items: row.items.map((item) => ({
    itemIndex: item.item_index,
    traceId: item.trace_id,
    status: item.status,
    scores: item.scores,
    scoreReasons: item.score_reasons ?? {},
    inputPreview: item.input_preview,
    outputPreview: item.output_preview,
    input: item.input,
    output: item.output ?? null,
    expectedOutput: item.expected_output,
    evaluation: mapItemEvaluation(item.evaluation),
    error: item.error,
    demoCase: item.demo_case ?? null,
  })),
  runHistory: row.run_history.map((point) => ({
    runId: point.run_id,
    label: point.label,
    completedAt: point.completed_at,
    successRate: point.success_rate,
    correctness: point.correctness,
    metricName: point.metric_name,
    scores: point.scores,
    traceCount: point.trace_count,
  })),
  experimentScores: row.experiment_scores.map((point) => ({
    agentId: point.agent_id,
    datasetId: point.dataset_id,
    runId: point.run_id,
    label: point.label,
    correctness: point.correctness,
    availability: point.availability,
    metricName: point.metric_name,
    selected: point.selected,
  })),
  modelTrends: (row.model_trends ?? []).map((series) => ({
    agentId: series.agent_id,
    agentName: series.agent_name,
    model: series.model,
    points: series.points.map((point) => ({
      runId: point.run_id,
      comparisonId: point.comparison_id ?? null,
      label: point.label,
      createdAt: point.created_at,
      correctness: point.correctness,
      availability: point.availability,
      selected: point.selected,
    })),
  })),
});

const toReferencePayload = (row: MetricReferenceResponse): MetricReferencePayload => ({
  agentId: row.agent_id,
  datasetId: row.dataset_id,
  thresholds: row.thresholds.map((threshold) => ({
    metric: threshold.metric,
    window: threshold.window,
    value: threshold.value,
    enabled: threshold.enabled,
  })),
  updatedAt: row.updated_at,
  updatedBy: row.updated_by,
});

export const metricsApi = {
  async listPairs(): Promise<MonitoredPair[]> {
    const pairs = await listPairsRaw();
    return pairs.map((pair) => ({
      id: pairKey(pair.agent_id, pair.dataset_id),
      label: `${pair.agent_id} + ${pair.dataset_name || pair.dataset_id}`,
    }));
  },

  async listDashboardPairs(): Promise<{ pairs: MonitoredPair[]; langfuseHost: string | null }> {
    const res = await apiRequest<{
      data: Array<{
        agent_id: string;
        dataset_id: string;
        dataset_name: string | null;
        label: string;
      }>;
      count: number;
      langfuse_host: string | null;
    }>("GET", "/api/metrics/dashboard-pairs");
    return {
      pairs: res.data.map((pair) => ({
        id: `${pair.agent_id}::${pair.dataset_id}`,
        label: pair.label,
        datasetName: pair.dataset_name,
      })),
      langfuseHost: res.langfuse_host,
    };
  },

  async getPairMetrics(pairId: string): Promise<PairMetricsPayload> {
    const pairs = await listPairsRaw();
    const { agentId, datasetId } = parsePairId(pairId);
    const pair = pairs.find((row) => row.agent_id === agentId && row.dataset_id === datasetId);
    if (!pair) return { nightly: false, liveMetrics: [] };

    const metrics = await apiRequest<PairMetricsResponse>(
      "GET",
      pairMetricsPath(pair.agent_id, pair.dataset_id),
    );

    return toPayload(metrics);
  },

  async getPairDashboard(
    pairId: string,
    runId?: string | null,
    includeItems = false,
    deferScores = false,
  ): Promise<PairDashboardPayload | null> {
    const { agentId, datasetId } = parsePairId(pairId);
    const params = new URLSearchParams();
    if (runId?.trim()) params.set("run_id", runId.trim());
    if (includeItems) params.set("include_items", "true");
    if (deferScores) params.set("defer_scores", "true");
    const qs = params.toString() ? `?${params.toString()}` : "";
    const dashboard = await apiRequest<PairDashboardResponse>(
      "GET",
      `${pairMetricsPath(agentId, datasetId)}/dashboard${qs}`,
    );
    return toDashboardPayload(dashboard);
  },

  async getPairRunItems(
    pairId: string,
    runId: string,
    page = 1,
    limit = 25,
  ): Promise<{
    items: PairDashboardItem[];
    total: number;
    page: number;
    limit: number;
    correctnessMetric: string | null;
  }> {
    const { agentId, datasetId } = parsePairId(pairId);
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit),
    });
    const res = await apiRequest<{
      items: Array<{
        item_index: number;
        trace_id: string | null;
        status: string;
        scores: Record<string, number>;
        score_reasons?: Record<string, string>;
        input_preview: string | null;
        output_preview: string | null;
        input?: unknown;
        output?: string | null;
        expected_output?: unknown;
        evaluation?: ItemEvaluationResponse | null;
        error: string | null;
        demo_case: string | null;
      }>;
      total: number;
      page: number;
      limit: number;
      correctness_metric: string | null;
    }>(
      "GET",
      `${pairMetricsPath(agentId, datasetId)}/runs/${encodeURIComponent(runId)}/items?${params.toString()}`,
    );
    return {
      items: res.items.map((item) => ({
        itemIndex: item.item_index,
        traceId: item.trace_id,
        status: item.status,
        scores: item.scores,
        scoreReasons: item.score_reasons ?? {},
        inputPreview: item.input_preview,
        outputPreview: item.output_preview,
        input: item.input,
        output: item.output ?? null,
        expectedOutput: item.expected_output,
        evaluation: mapItemEvaluation(item.evaluation),
        error: item.error,
        demoCase: item.demo_case ?? null,
      })),
      total: res.total,
      page: res.page,
      limit: res.limit,
      correctnessMetric: res.correctness_metric,
    };
  },

  async getPairRunCost(pairId: string, runId: string): Promise<number | null> {
    const { agentId, datasetId } = parsePairId(pairId);
    const res = await apiRequest<{ cost_usd: number | null }>(
      "GET",
      `${pairMetricsPath(agentId, datasetId)}/runs/${encodeURIComponent(runId)}/cost`,
    );
    return res.cost_usd;
  },

  async getDatasetComparisons(datasetId: string): Promise<ModelComparisonGroup[]> {
    const response = await apiRequest<{
      data: Array<{
        comparison_id: string;
        dataset_id: string;
        created_at: string;
        models: Array<{
          agent_id: string;
          agent_name: string | null;
          model: string | null;
          run_id: string;
          status: string;
          correctness: number | null;
          availability: number | null;
          item_count: number;
          is_winner: boolean;
        }>;
      }>;
      count: number;
    }>("GET", `/api/metrics/datasets/${encodeURIComponent(datasetId)}/comparisons`);
    return response.data.map((group) => ({
      comparisonId: group.comparison_id,
      datasetId: group.dataset_id,
      createdAt: group.created_at,
      models: group.models.map((model) => ({
        agentId: model.agent_id,
        agentName: model.agent_name,
        model: model.model,
        runId: model.run_id,
        status: model.status,
        correctness: model.correctness,
        availability: model.availability,
        itemCount: model.item_count,
        isWinner: model.is_winner,
      })),
    }));
  },

  async getPairReference(pairId: string): Promise<MetricReferencePayload> {
    const { agentId, datasetId } = parsePairId(pairId);
    const reference = await apiRequest<MetricReferenceResponse>(
      "GET",
      `${pairMetricsPath(agentId, datasetId)}/reference`,
    );
    return toReferencePayload(reference);
  },

  async savePairReference(
    pairId: string,
    thresholds: MetricReferenceThreshold[],
  ): Promise<MetricReferencePayload> {
    const { agentId, datasetId } = parsePairId(pairId);
    const reference = await apiRequest<MetricReferenceResponse>(
      "PUT",
      `${pairMetricsPath(agentId, datasetId)}/reference`,
      { body: { thresholds } },
    );
    return toReferencePayload(reference);
  },

  async deletePairReference(pairId: string): Promise<{ deleted: boolean }> {
    const { agentId, datasetId } = parsePairId(pairId);
    return apiRequest<{ deleted: boolean }>(
      "DELETE",
      `${pairMetricsPath(agentId, datasetId)}/reference`,
    );
  },

  async refreshPairMetrics(pairId: string): Promise<PairMetricsPayload> {
    const { agentId, datasetId } = parsePairId(pairId);
    const report = await apiRequest<MetricsAggregationReport>("POST", "/api/metrics/aggregate");
    const next = await metricsApi.getPairMetrics(pairId);
    const entry = report.entries.find((row) => row.agent_id === agentId && row.dataset_id === datasetId);
    if (!entry) return next;

    return {
      ...next,
      refreshOutcome: entry.outcome,
      refreshMessage:
        entry.outcome === "written"
          ? `Updated ${entry.metric_count ?? next.liveMetrics.length} metric(s) from Langfuse.`
          : entry.reason ?? `Aggregation skipped with outcome '${entry.outcome}'.`,
    };
  },

  async refreshPairDashboard(pairId: string): Promise<PairDashboardPayload | null> {
    await apiRequest<MetricsAggregationReport>("POST", "/api/metrics/aggregate");
    return metricsApi.getPairDashboard(pairId);
  },

  async savePairNightly(pairId: string, nightly: boolean): Promise<PairMetricsPayload> {
    const pairs = await listPairsRaw();
    const { agentId, datasetId } = parsePairId(pairId);
    const pair = pairs.find((row) => row.agent_id === agentId && row.dataset_id === datasetId);
    if (!pair) throw new Error("Pair not found.");

    await apiRequest<PairRow>("POST", "/api/pairs/connect", {
      body: {
        agent_id: pair.agent_id,
        dataset_id: pair.dataset_id,
        dataset_name: pair.dataset_name,
        nightly,
      },
    });

    return metricsApi.getPairMetrics(pairId);
  },
};
